// src/components/wordbook/WorkbookList.tsx
import { FC, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Chip,
    Alert,
    Typography,
    FormControl,
    InputLabel,
    InputAdornment,
    Select,
    MenuItem,
    Checkbox,
    Menu,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    VisibilityOff,
    Edit,
    Delete,
    Restore,
    DeleteForever,
    FolderOpen,
    Search as SearchIcon,
} from '@mui/icons-material';
import { Visibility } from '../../types/wordbook';
import { useNavigate } from 'react-router-dom';
import {
    useWordbookList,
    useDeleteWordbook,
    usePermanentDeleteWordbook,
    useRestoreWordbook,
    useToggleWordbookVisibility,
    useBulkDeleteWordbooks,
    useBulkUpdateVisibility,
} from '../../hooks/useWordbook';
import { formatKstDate } from '../../utils/dateTime';

export const WordbookList: FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [visibilityFilter, setVisibilityFilter] = useState<Visibility | ''>('');
    const [isLibraryFilter, setIsLibraryFilter] = useState<boolean | ''>('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    // 데이터 조회
    const { data, isLoading, error } = useWordbookList({
        page: page + 1,
        size,
        searchKeyword: search || undefined,
        visibility: visibilityFilter || undefined,
        isLibrary: isLibraryFilter === '' ? undefined : isLibraryFilter,
        sortBy: 'createdAt',
        sortDirection: 'desc',
    });

    useEffect(() => {
        console.log('📚 Wordbook 데이터:', data);
    }, [data]);

    const triggerSearch = () => {
        setSearch(searchInput.trim());
        setPage(0);
    };

    // Mutations
    const deleteWordbook = useDeleteWordbook();
    const permanentDelete = usePermanentDeleteWordbook();
    const restoreWordbook = useRestoreWordbook();
    const toggleVisibility = useToggleWordbookVisibility();
    const bulkDelete = useBulkDeleteWordbooks();
    const bulkUpdateVisibility = useBulkUpdateVisibility();

    const handleToggleVisibility = (id: number) => {
        toggleVisibility.mutate(id);
    };

    const handleDelete = (id: number) => {
        if (confirm('단어장을 삭제하시겠습니까?')) {
            deleteWordbook.mutate(id);
        }
    };

    const handlePermanentDelete = (id: number) => {
        if (confirm('⚠️ 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
            permanentDelete.mutate(id);
        }
    };

    const handleRestore = (id: number) => {
        restoreWordbook.mutate(id);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedIds(wordbooks.map(w => w.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        if (confirm(`선택한 ${selectedIds.length}개의 단어장을 삭제하시겠습니까?`)) {
            bulkDelete.mutate(selectedIds, {
                onSuccess: () => {
                    setSelectedIds([]);
                },
            });
        }
    };

    const handleBulkVisibility = (visibility: Visibility) => {
        if (selectedIds.length === 0) return;
        bulkUpdateVisibility.mutate(
            { wordbookIds: selectedIds, visibility },
            {
                onSuccess: () => {
                    setSelectedIds([]);
                    setAnchorEl(null);
                },
            }
        );
    };

    const wordbooks = data?.content || [];
    const totalCount = data?.totalCount || 0;

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    단어장 관리
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate('/wordbook/create')}
                >
                    새 단어장 추가
                </Button>
            </Box>

            <Card>
                <CardContent>
                    {/* 필터 영역 */}
                    <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                        <TextField
                            label="검색"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    triggerSearch();
                                }
                            }}
                            size="small"
                            sx={{ width: 250 }}
                            placeholder="제목 검색 후 Enter"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={triggerSearch}
                                                edge="end"
                                            >
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <FormControl size="small" sx={{ width: 150 }}>
                            <InputLabel>노출 상태</InputLabel>
                            <Select
                                value={visibilityFilter}
                                onChange={(e) => {
                                    setVisibilityFilter(e.target.value as Visibility | '');
                                    setPage(0);
                                }}
                                label="노출 상태"
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value={Visibility.VISIBLE}>노출</MenuItem>
                                <MenuItem value={Visibility.HIDDEN}>숨김</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ width: 150 }}>
                            <InputLabel>라이브러리</InputLabel>
                            <Select
                                value={isLibraryFilter}
                                onChange={(e) => {
                                    setIsLibraryFilter(e.target.value as boolean | '');
                                    setPage(0);
                                }}
                                label="라이브러리"
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="true">라이브러리</MenuItem>
                                <MenuItem value="false">일반</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* 일괄 작업 버튼 */}
                    {selectedIds.length > 0 && (
                        <Box display="flex" gap={1} mb={2}>
                            <Chip
                                label={`${selectedIds.length}개 선택됨`}
                                onDelete={() => setSelectedIds([])}
                                color="primary"
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleBulkDelete}
                                color="error"
                            >
                                일괄 삭제
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                            >
                                일괄 노출 설정
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                            >
                                <MenuItem onClick={() => handleBulkVisibility(Visibility.VISIBLE)}>
                                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>노출</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={() => handleBulkVisibility(Visibility.HIDDEN)}>
                                    <ListItemIcon><VisibilityOff fontSize="small" /></ListItemIcon>
                                    <ListItemText>숨김</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error.message || '데이터를 불러오는데 실패했습니다.'}
                        </Alert>
                    )}

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedIds.length === wordbooks.length && wordbooks.length > 0}
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < wordbooks.length}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                </TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell>제목</TableCell>
                                <TableCell>소유자</TableCell>
                                <TableCell>그룹</TableCell>
                                <TableCell>언어</TableCell>
                                <TableCell>단어 수</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>노출</TableCell>
                                <TableCell>라이브러리</TableCell>
                                <TableCell>생성일</TableCell>
                                <TableCell>액션</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center">
                                        <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                                            <CircularProgress size={28} />
                                            <Typography variant="body2" color="text.secondary">
                                                단어장 목록 로딩 중...
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : wordbooks.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center">
                                        <Typography color="text.secondary" py={4}>
                                            등록된 단어장이 없습니다.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                wordbooks.map((wordbook) => (
                                    <TableRow
                                        key={wordbook.id}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            opacity: wordbook.deletedAt ? 0.5 : 1,
                                            backgroundColor: wordbook.deletedAt ? '#ffebee' : 'inherit',
                                        }}
                                        onClick={() => navigate(`/wordbook/${wordbook.id}`)}
                                    >
                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedIds.includes(wordbook.id)}
                                                onChange={(e) => handleSelectOne(wordbook.id, e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell>{wordbook.id}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {wordbook.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {wordbook.ownerName || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {wordbook.groupTitle ? (
                                                <Chip
                                                    icon={<FolderOpen />}
                                                    label={wordbook.groupTitle}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {wordbook.headLang} → {wordbook.meaningLang}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={wordbook.totalWords.toLocaleString()}
                                                size="small"
                                                color={wordbook.totalWords > 0 ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {wordbook.deletedAt ? (
                                                <Chip label="삭제됨" size="small" color="error" />
                                            ) : (
                                                <Chip label="활성" size="small" color="success" />
                                            )}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {!wordbook.deletedAt && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleVisibility(wordbook.id)}
                                                >
                                                    {wordbook.visibility === Visibility.VISIBLE ? (
                                                        <VisibilityIcon color="primary" />
                                                    ) : (
                                                        <VisibilityOff />
                                                    )}
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {wordbook.libraryId != null && (
                                                <Chip label="라이브러리" size="small" color="secondary" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatKstDate(wordbook.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Box display="flex" gap={0.5}>
                                                {wordbook.deletedAt ? (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRestore(wordbook.id)}
                                                            title="복원"
                                                            color="success"
                                                        >
                                                            <Restore />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handlePermanentDelete(wordbook.id)}
                                                            title="영구 삭제"
                                                            color="error"
                                                        >
                                                            <DeleteForever />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => navigate(`/wordbook/${wordbook.id}/edit`)}
                                                            title="수정"
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(wordbook.id)}
                                                            title="삭제"
                                                            color="error"
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => {
                            setSize(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="페이지당 행 수:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} / 전체 ${count !== -1 ? count : `${to} 이상`}`
                        }
                    />
                </CardContent>
            </Card>
        </Box>
    );
};
